import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Package, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import ProductCard from "@/components/ProductCard";

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);

  const page = parseInt(searchParams.get('page') || '1');
  const categoryId = searchParams.get('category');
  const search = searchParams.get('search');
  const sort = searchParams.get('sort') || 'name';
  const manufacturerId = searchParams.get('manufacturer');
  const inStock = searchParams.get('in_stock') === 'true';
  const minPrice = searchParams.get('min_price');
  const maxPrice = searchParams.get('max_price');
  useSEO(search ? `Hledani: ${search}` : 'Katalog produktu', `Kancelarske potreby - ${total > 0 ? total.toLocaleString() : '35 000+'} produktu.`);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('limit', '24');
    params.set('sort', sort);
    if (categoryId) params.set('category_id', categoryId);
    if (search) params.set('search', search);
    if (manufacturerId) params.set('manufacturer_id', manufacturerId);
    if (inStock) params.set('in_stock', 'true');
    if (minPrice) params.set('min_price', minPrice);
    if (maxPrice) params.set('max_price', maxPrice);

    axios.get(`${API}/products?${params.toString()}`)
      .then(res => { setProducts(res.data.products); setTotal(res.data.total); setPages(res.data.pages); })
      .catch(console.error).finally(() => setLoading(false));
  }, [page, categoryId, search, sort, manufacturerId, inStock, minPrice, maxPrice]);

  useEffect(() => {
    axios.get(`${API}/categories?flat=true`).then(res => setCategories(res.data)).catch(console.error);
    axios.get(`${API}/manufacturers`).then(res => setManufacturers(res.data)).catch(console.error);
  }, []);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) { params.set(key, value); } else { params.delete(key); }
    params.set('page', '1');
    setSearchParams(params);
  };

  const clearAllFilters = () => setSearchParams({});
  const hasFilters = categoryId || manufacturerId || search || inStock || minPrice || maxPrice;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="products-page">
      <div className="flex flex-col md:flex-row gap-6">
        <aside className="w-full md:w-64 flex-shrink-0">
          <div className="bg-white border border-slate-200 rounded-md p-4 sticky top-32">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800">Filtry</h3>
              {hasFilters && <button onClick={clearAllFilters} className="text-xs text-[#004E98] hover:underline">Zrusit vse</button>}
            </div>
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={inStock} onChange={(e) => updateFilter('in_stock', e.target.checked ? 'true' : '')} className="w-4 h-4 text-[#004E98] rounded border-slate-300 focus:ring-[#004E98]" />
                <span className="text-sm font-medium text-slate-700">Pouze skladem</span>
              </label>
            </div>
            <div className="mb-4">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Cena (Kc)</label>
              <div className="flex items-center gap-2">
                <input type="number" placeholder="Od" value={minPrice || ''} onChange={(e) => updateFilter('min_price', e.target.value)} className="w-full h-9 border border-slate-300 rounded-sm px-2 text-sm focus:border-[#004E98] outline-none" />
                <span className="text-slate-400">-</span>
                <input type="number" placeholder="Do" value={maxPrice || ''} onChange={(e) => updateFilter('max_price', e.target.value)} className="w-full h-9 border border-slate-300 rounded-sm px-2 text-sm focus:border-[#004E98] outline-none" />
              </div>
            </div>
            <div className="mb-4">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Kategorie</label>
              <select value={categoryId || ''} onChange={(e) => updateFilter('category', e.target.value)} className="w-full h-10 border border-slate-300 rounded-sm px-3 text-sm focus:border-[#004E98] outline-none" data-testid="category-filter">
                <option value="">Vsechny kategorie</option>
                {categories.filter(c => c.level_depth <= 2).map(cat => (
                  <option key={cat.id_category} value={cat.id_category}>{cat.name?.replace(/^\d+\./, '').trim()}</option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Vyrobce</label>
              <select value={manufacturerId || ''} onChange={(e) => updateFilter('manufacturer', e.target.value)} className="w-full h-10 border border-slate-300 rounded-sm px-3 text-sm focus:border-[#004E98] outline-none" data-testid="manufacturer-filter">
                <option value="">Vsichni vyrobci</option>
                {manufacturers.map(m => (<option key={m.id_manufacturer} value={m.id_manufacturer}>{m.name}</option>))}
              </select>
            </div>
            <div className="mb-4">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Razeni</label>
              <select value={sort} onChange={(e) => updateFilter('sort', e.target.value)} className="w-full h-10 border border-slate-300 rounded-sm px-3 text-sm focus:border-[#004E98] outline-none" data-testid="sort-filter">
                <option value="name">Nazev A-Z</option>
                <option value="price_asc">Cena: nejnizsi</option>
                <option value="price_desc">Cena: nejvyssi</option>
                <option value="newest">Nejnovejsi</option>
              </select>
            </div>
          </div>
        </aside>

        <main className="flex-1">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-slate-800">{search ? `Vysledky pro "${search}"` : 'Produkty'}</h1>
            <p className="text-sm text-slate-500">{total.toLocaleString()} produktu</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div>
          ) : products.length === 0 ? (
            <div className="text-center py-12 text-slate-500"><Package className="w-16 h-16 mx-auto mb-4 text-slate-300" /><p>Zadne produkty nenalezeny</p></div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map(product => (<ProductCard key={product.id_product} product={product} />))}
              </div>
              {pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  {Array.from({ length: Math.min(pages, 10) }, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => updateFilter('page', p.toString())} className={`w-10 h-10 rounded-sm text-sm font-medium transition-colors ${page === p ? 'bg-[#004E98] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-[#004E98]'}`}>{p}</button>
                  ))}
                  {pages > 10 && <span className="text-slate-400">...</span>}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
